import React, { createContext, useContext, useRef, useState, useEffect } from "react";

import { noop } from "lib/utils";
import useConfig from "hooks/useConfig";
import useStableCallback from "hooks/useStableCallback";

const ModalContext = createContext();

export const ModalProvider = ({ children }) => {
  const { closeModal, content, modal, options, showModal } = useConfigModal();

  return (
    <ModalContext.Provider value={{ closeModal, content, modal, options, showModal }}>
      {children}
    </ModalContext.Provider>
  );
};

export const useConfigModal = () => {
  // Be careful enabling `closeModalWithBackButton` - it will interfere with the global session history.
  // Whether in an iframe, or a <script> tag, this could have unintended consequences if the host
  // page is managing its own routing with history. It would be safe to enable in the context of a
  // native app. It is exposed as a config parameter to allow customers to enable it themselves.
  const { closeModalWithBackButton } = useConfig();
  const onCloseRef = useRef(noop);

  const [modal, setModal] = useState(false);
  const [content, setContent] = useState(null);
  const [options, setOptions] = useState({});
  const lastFocus = useRef();

  const closeModal = useStableCallback(() => {
    if (onCloseRef.current && typeof onCloseRef.current === "function") {
      onCloseRef.current(); // Call a custom close handler
      onCloseRef.current = noop; // reset the handler
    }
    setModal(false);
    if (closeModalWithBackButton) {
      window.history.back();
    }
  });

  const onPopState = useStableCallback((event) => {
    // if popstate occurs while modal is open, it's likely to be because the user pressed the
    // browser back button. We will make sure that closes the modal.
    // `closeModal()` goes "back" itself, so we will push once more onto the history
    // so that we are back in the same state as after `showModal()`
    if (closeModalWithBackButton && modal) {
      window.history.pushState(event.state, null);
      closeModal();
    } else {
      // probably a pushstate happened
    }
  });

  useEffect(() => {
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [onPopState]);

  const showModal = (
    content,
    {
      fixedHeight = false,
      takeover = false,
      takeoverTablet = false,
      autoFocus = true,
      restoreFocus = true,
      onClose = noop,
      classNames = {},
    } = {}
  ) => {
    setModal(true);
    setContent(content);
    setOptions({
      fixedHeight,
      takeover,
      takeoverTablet,
      autoFocus,
      restoreFocus,
      classNames
    });

    onCloseRef.current = onClose;
    lastFocus.current =
      document.activeElement?.shadowRoot?.activeElement ||
      document.activeElement;

    if (closeModalWithBackButton) {
      // When opening the 1st modal, we push onto the history
      // so that if the user presses back while there is an open
      // modal, we stay on the current route. We will clean this up later
      // when the last modal is closed.
      window.history.pushState(window.history.state, null);
    }
  }

  // restore focus after the modal has closed
  useEffect(() => {
    if (!modal && options.restoreFocus) {
      lastFocus.current?.focus();
      lastFocus.current = null;
    }
  }, [modal, options.restoreFocus]);

  return { content, closeModal, modal, options, showModal };
};

const useModal = () => {
  const context = useContext(ModalContext);

  if (context === undefined) {
    throw new Error("useModal must be used within a ModalContext");
  }

  return { ...context };
};

export default useModal;
